import type { GatewayBrowserClient } from "../gateway.ts";

export type HeartbeatAnalyticsState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  hbLoading: boolean;
  hbError: string | null;
  hbHistory: Array<{
    seq: number;
    ts: number;
    status: string;
    reason?: string;
    channel?: string;
    preview?: string;
    durationMs?: number;
    hasMedia?: boolean;
    silent?: boolean;
    indicatorType?: string;
    accountId?: string;
  }>;
  hbStats: Record<string, unknown> | null;
  hbLiveEvents: Array<Record<string, unknown>>;
  hbLatestSeq: number;
  hbPaused: boolean;
  hbTimeRange: "1h" | "6h" | "24h";
};

const TIME_RANGE_MS: Record<"1h" | "6h" | "24h", number> = {
  "1h": 3600000,
  "6h": 21600000,
  "24h": 86400000,
};

export async function loadHeartbeatAnalytics(state: HeartbeatAnalyticsState) {
  if (!state.client || !state.connected || state.hbLoading) {
    return;
  }
  state.hbLoading = true;
  state.hbError = null;
  const sinceMs = TIME_RANGE_MS[state.hbTimeRange];
  try {
    const [historyRes, statsRes] = await Promise.all([
      state.client.request("heartbeat.history", { sinceMs, limit: 100 }),
      state.client.request("heartbeat.stats", { sinceMs }),
    ]);
    const history = historyRes as {
      events?: HeartbeatAnalyticsState["hbHistory"];
      latestSeq?: number;
    };
    state.hbHistory = Array.isArray(history?.events) ? history.events : [];
    state.hbLatestSeq = typeof history?.latestSeq === "number" ? history.latestSeq : 0;
    state.hbStats = statsRes as Record<string, unknown>;
    state.hbLiveEvents = [];
  } catch (err) {
    state.hbError = String(err);
  } finally {
    state.hbLoading = false;
  }
}

let hbPollTimer: ReturnType<typeof setInterval> | null = null;

/** Start auto-polling for new heartbeat events (every 15 seconds) */
export function startHeartbeatAutoRefresh(
  state: HeartbeatAnalyticsState,
  callback: () => void,
): void {
  stopHeartbeatAutoRefresh();
  hbPollTimer = setInterval(async () => {
    await pollHeartbeatIncremental(state);
    callback();
  }, 15_000);
}

/** Stop heartbeat auto-polling */
export function stopHeartbeatAutoRefresh(): void {
  if (hbPollTimer) {
    clearInterval(hbPollTimer);
    hbPollTimer = null;
  }
}

export async function pollHeartbeatIncremental(state: HeartbeatAnalyticsState) {
  if (state.hbPaused || !state.client || !state.connected) {
    return;
  }
  try {
    const res = (await state.client.request("heartbeat.history", {
      afterSeq: state.hbLatestSeq,
      limit: 20,
    })) as { events?: Array<Record<string, unknown>>; latestSeq?: number };
    const events = Array.isArray(res?.events) ? res.events : [];
    if (events.length > 0) {
      state.hbLiveEvents = [...events, ...state.hbLiveEvents].slice(0, 50);
      if (typeof res?.latestSeq === "number") {
        state.hbLatestSeq = res.latestSeq;
      }
    }
  } catch {
    /* ignore incremental poll errors */
  }
}
